import { ComponentType, PropsWithChildren, ReactNode, createContext, useContext } from 'react';
import {
  ConditionLoadingTriggerButton as ConditionValueLoadingSelect,
  ConditionSingleValueStringInput as ConditionValueSingleStringInput,
  ConditionSingleValueNumberInput as ConditionValueSingleNumberInput,
  ConditionMultiValueStringInput as ConditionValueMultiStringInput,
  ConditionSingleValueStringSelect as ConditionValueSingleStringSelect,
  ConditionSingleValueStringSelectWithButton as ConditionValueSingleStringSelectWithButton,
  ConditionMultiValueStringSelect as ConditionValueMultiStringSelect,
  ConditionSingleValueComparatorSelect as ConditionComparatorSelect,
  ConditionGeoLocation,
  ConditionCastTypeSelect,
  ConditionDatePicker as ConditionValueDateSelect,
  ConditionDateRangePicker as ConditionValueDateRangeSelect,
  ConditionLayout,
  AddConditionButton,
  AddCriteriaButton,
  CriteriaCombinatorSelect,
  CriteriaLayout,
  DuplicateCriteriaButton,
  QueryCombinatorSelect,
  QueryLayout,
  RedirectButton,
  RemoveConditionButton,
  RemoveCriteriaButton,
  RemoveRequirementButton,
  RequirementCountFilter as RequirementCountFilterInput,
  RequirementLayout,
  SingleValueEventSelect as EventSelect,
  SingleValueUserProfileSelect as UserProfileSelect,
  DuplicateRequirementButton,
  DuplicateConditionButton,
  RedoButton,
  UndoButton,
  LoadingState as QueryBuilderLoadingState,
} from 'ui/queryBuilder';
import { SelectValueType } from 'ui/selects';
import { BaseOption } from 'ui/selectv2/types';
import { NoOpComponent } from 'utils/reactHelpers';

import { DateRange, GeoLocationValue, MultiGeoLocationValue } from '../types';
import { CastType } from '../types/castTypes';
import { ClientDataType } from '../types/clientDataType';
import { Combinator } from '../types/combinators';
import { AnyComparator, Comparator } from '../types/comparators';
import { CountFilter, CountFilterType } from '../types/countFilter';
import { FieldType } from '../types/fieldTypes';

export type ButtonProps = {
  onClick: () => void;
  isDisabled: boolean;
};

export type ButtonWithMenuProps = ButtonProps;

export type RedirectButtonProps = Partial<ButtonProps> &
  Partial<{
    link: string;
    tooltipContent: string;
  }>;

export type InputProps<T> = {
  onChange: (value: T) => void;
  value: T;
  hasError: boolean;
  hasWarning?: boolean;
  isDisabled: boolean;
  placeholder: string;
};

type ValueInputProps<T> = InputProps<T> & {
  dataType: ClientDataType;
  field: string;
  fieldType: FieldType;
  comparator?: Comparator;
};

export type SingleValueInputProps<T> = ValueInputProps<T | null>;

export type MultiValueInputProps<T> = ValueInputProps<T[] | null> & { optionsLimit?: number };

export type SelectOption<T> = {
  title: string;
  subtitle?: string;
  badge?: string;
  icon?: ReactNode;
  isDisabled?: boolean;
  tooltipContent?: string;
  value: T;
};

export type SelectProps<T> = {
  canFetch?: boolean;
  isEmpty?: boolean;
  isLoading?: boolean;
  isVirtualized?: boolean;
  onFetch?: () => void;
  onSelectChange?: (newSelect: BaseOption<SelectValueType>[]) => void;
  options?: SelectOption<T>[];
  search?: string;
  setSearch?: (search: string) => void;
  shouldHideDisplayValues?: boolean;
  triggerContent?: string;
  isOpenByDefault?: boolean;
};

export type SingleValueSelectProps<T> = SingleValueInputProps<T> & SelectProps<T>;

export type RequirementSelectProps<T> = SelectProps<T> & InputProps<T | null>;

export type MultiValueSelectProps<T> = MultiValueInputProps<T> & SelectProps<T>;

export type DateSelectProps<T> = SingleValueInputProps<T> & Pick<SelectProps<T>, 'isOpenByDefault'>;

export type ComparatorInputProps = InputProps<AnyComparator | null> &
  SelectProps<AnyComparator> & {
    dataType: ClientDataType;
    field: string;
    fieldType: FieldType;
  };

export type EventSelectProps = RequirementSelectProps<ClientDataType> & {
  isInRequirement: boolean;
};
export type UserProfileSelectProps = RequirementSelectProps<string>;

export type FieldNameInputProps = InputProps<string | null> &
  SelectProps<string> & {
    dataType: ClientDataType;
  };

export type ConditionLayoutProps = {
  AddCondition: ReactNode;
  FieldSelect: ReactNode;
  CastTypeSelect?: ReactNode;
  ComparatorSelect: ReactNode;
  ValueInput: ReactNode;
  Delete: ReactNode;
  Duplicate: ReactNode;
};

export type RequirementLayoutProps = {
  dataType: ClientDataType;
  combinator: Combinator;
  Conditions: ReactNode[];
  Add?: ReactNode;
  Delete: ReactNode;
  Duplicate?: ReactNode;
  EventSelect?: ReactNode;
  CountFilter?: ReactNode;
  CombinatorSelect?: ReactNode;
  isCatalogESIndex?: boolean;
};

export type CriteriaLayoutProps = {
  combinator: Combinator;
  CombinatorSelect: ReactNode;
  Delete?: ReactNode;
  Duplicate: ReactNode;
  EventSelect: ReactNode;
  isFirstElement: boolean;
  isReadOnly: boolean;
  queryCombinator: Combinator;
  Requirements: ReactNode[];
  UserProfileSelect: ReactNode;
};

export type QueryLayoutProps = {
  Add: ReactNode;
  AITools?: ReactNode;
  CombinatorSelect: ReactNode;
  Criteria: ReactNode[];
  EditReadSelect?: ReactNode;
  UndoButton?: ReactNode;
  RedoButton?: ReactNode;
};

export type VisualComponentsContextState = {
  /**
   * Condition Builder Components
   */

  // String Value Inputs
  ConditionValueSingleStringInput: ComponentType<SingleValueSelectProps<string>>;
  ConditionValueMultiStringInput: ComponentType<MultiValueSelectProps<string>>;
  ConditionValueSingleStringSelect: ComponentType<SingleValueSelectProps<string>>;
  ConditionValueMultiStringSelect: ComponentType<MultiValueSelectProps<string>>;
  ConditionValueSingleStringSelectWithButton: ComponentType<
    SingleValueSelectProps<string> & RedirectButtonProps
  >;
  ConditionValueLoadingSelect: ComponentType;

  // Number Value Inputs
  ConditionValueSingleNumberInput: ComponentType<SingleValueSelectProps<string>>;
  ConditionCastTypeSelect: ComponentType<SingleValueSelectProps<CastType>>;

  // Date Value Inputs
  ConditionValueDateSelect: ComponentType<DateSelectProps<string>>;
  ConditionValueDateRangeSelect: ComponentType<DateSelectProps<DateRange>>;

  // Geo-Location Value Inputs
  ConditionValueSingleGeoLocationValue: ComponentType<SingleValueInputProps<GeoLocationValue>>;
  ConditionValueMultiGeoLocationValue: ComponentType<SingleValueInputProps<MultiGeoLocationValue>>;

  // Other Condition Inputs
  ConditionComparatorSelect: ComponentType<ComparatorInputProps>;
  ConditionFieldSelect: ComponentType<FieldNameInputProps>;

  // Buttons
  AddConditionButton: ComponentType<ButtonProps>;
  DuplicateConditionButton: ComponentType<ButtonProps>;
  RemoveConditionButton: ComponentType<ButtonProps>;
  RedirectButton: ComponentType<RedirectButtonProps>;

  // Layout
  ConditionLayout: ComponentType<ConditionLayoutProps>;

  /**
   * Requirement Builder Components
   */

  // Inputs
  RequirementCombinatorSelect: ComponentType<
    InputProps<Combinator | null> & SelectProps<Combinator | null>
  >;
  RequirementCountFilterInput: ComponentType<
    InputProps<CountFilter> & {
      countFilterType: CountFilterType;
      setCountFilterType: (newCountFilter: CountFilterType) => void;
    }
  >;

  // Buttons
  DuplicateRequirementButton: ComponentType<ButtonWithMenuProps>;
  RemoveRequirementButton: ComponentType<ButtonProps>;

  // Layout
  RequirementLayout: ComponentType<RequirementLayoutProps>;

  /**
   * Criteria Builder Components
   */

  // Inputs
  CriteriaCombinatorSelect: ComponentType<InputProps<Combinator | null>>;

  // Buttons
  AddCriteriaButton: ComponentType<ButtonProps>;
  DuplicateCriteriaButton: ComponentType<ButtonProps>;
  RemoveCriteriaButton: ComponentType<ButtonProps>;

  // Layout
  CriteriaLayout: ComponentType<CriteriaLayoutProps>;

  /**
   * Query Builder Components
   */

  // Buttons
  UndoQueryButton: ComponentType<ButtonProps>;
  RedoQueryButton: ComponentType<ButtonProps>;

  // Inputs
  QueryCombinatorSelect: ComponentType<InputProps<Combinator | null>>;
  EditReadSelect: ComponentType<Omit<InputProps<boolean>, 'placeholder'>>;

  // Layout
  QueryLayout: ComponentType<QueryLayoutProps>;

  // Selects in multiple layers
  EventSelect: ComponentType<EventSelectProps>;
  UserProfileSelect: ComponentType<UserProfileSelectProps>;

  // Loading state
  QueryBuilderLoadingState: ComponentType;
};

// TODO: We'll add default visual components here as they're implemented
export const defaultState: VisualComponentsContextState = {
  ConditionValueSingleStringInput,
  ConditionValueMultiStringInput,
  ConditionValueSingleStringSelect,
  ConditionValueSingleStringSelectWithButton,
  ConditionValueMultiStringSelect,
  ConditionValueSingleNumberInput,
  ConditionCastTypeSelect,
  ConditionValueDateSelect,
  ConditionValueDateRangeSelect,
  ConditionValueSingleGeoLocationValue: ConditionGeoLocation,
  ConditionValueMultiGeoLocationValue: NoOpComponent, // not yet
  ConditionValueLoadingSelect,
  ConditionComparatorSelect,
  ConditionFieldSelect: ConditionValueSingleStringSelect,
  ConditionLayout,
  AddConditionButton,
  AddCriteriaButton,
  CriteriaCombinatorSelect,
  CriteriaLayout,
  DuplicateCriteriaButton,
  DuplicateRequirementButton: DuplicateRequirementButton,
  DuplicateConditionButton: DuplicateConditionButton,
  /**
   * EditReadSelect returns null by default.
   * To use EditReadSelect add the ui component (ui/queryBuilder/EditReadSelect) to your custom visual components context
   */
  EditReadSelect: NoOpComponent,
  EventSelect,
  QueryCombinatorSelect,
  QueryLayout,
  RedirectButton,
  RedoQueryButton: RedoButton,
  RemoveConditionButton,
  RemoveCriteriaButton,
  RemoveRequirementButton,
  /**
   * RequirementCombinatorSelect returns null by default.
   * To use RequirementCombinatorSelect add the ui component (import from ui/queryBuilder) to your custom visual components context
   */
  RequirementCombinatorSelect: NoOpComponent,
  RequirementCountFilterInput,
  RequirementLayout,
  UndoQueryButton: UndoButton,
  UserProfileSelect,
  QueryBuilderLoadingState,
};

const VisualComponentsContext = createContext(defaultState);

export const VisualComponentsContextProvider: ComponentType<
  PropsWithChildren<Partial<VisualComponentsContextState>>
> = ({ children, ...overrides }) => (
  <VisualComponentsContext.Provider value={{ ...defaultState, ...overrides }}>
    {children}
  </VisualComponentsContext.Provider>
);

export const useVisualComponentsContext = () => useContext(VisualComponentsContext);
